'use client';

import { Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, Radio, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState, ErrorState, LoadingState, SearchInput } from '@/components/shared';
import {
  Badge,
  Button,
  Card,
  SegmentedControl,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { useDeallocateDevice, usePartnerDevices } from '@/hooks/usePartnerDevices';
import type { PartnerDetail, PoolDevice } from '@/lib/types';
import { AllocateDialog } from './AllocateDialog';

type PoolState = 'all' | 'free' | 'allocated';

const STATES = [
  { value: 'all' as const, label: 'All' },
  { value: 'free' as const, label: 'Free' },
  { value: 'allocated' as const, label: 'Allocated' },
];

/**
 * The pool, from the Flespi registry rather than from observed telemetry — a box
 * that has been registered and never connected is exactly the one about to be
 * handed to a customer, and it has to be listed.
 */
export function PoolTable({ partner }: { partner: PartnerDetail }) {
  const inheritsFrom =
    partner.tokenHolder && partner.tokenHolder.id !== partner.id ? partner.tokenHolder : null;

  if (inheritsFrom) return <PoolLivesElsewhere holder={inheritsFrom} />;

  return <OwnPool partner={partner} />;
}

/**
 * A partner that sells on another's token has no inventory of its own: one
 * physical device must not be two pool rows, each allocatable to a different
 * customer. Allocation happens from the holder's pool, which may land in this
 * partner's customers.
 */
function PoolLivesElsewhere({ holder }: { holder: { id: string; name: string } }) {
  return (
    <EmptyState
      icon={Radio}
      title={`The pool lives with ${holder.name}`}
      description="This partner sells on that token, so the devices are listed there. Allocating from that pool can still hand a device to one of this partner's customers."
      action={
        <Button variant="outline" asChild>
          <Link to="/partners/$partnerId" params={{ partnerId: holder.id }}>
            Open {holder.name}
          </Link>
        </Button>
      }
    />
  );
}

function OwnPool({ partner }: { partner: PartnerDetail }) {
  const [state, setState] = useState<PoolState>('all');
  const [search, setSearch] = useState('');
  const [allocating, setAllocating] = useState<PoolDevice | null>(null);

  const { data, isLoading, error } = usePartnerDevices(partner.id, {
    state: state === 'all' ? undefined : state,
    search: search || undefined,
  });
  const deallocate = useDeallocateDevice(partner.id);

  const devices = data?.data ?? [];

  const release = async (device: PoolDevice) => {
    try {
      await deallocate.mutateAsync(device.id);
      toast.success(`${device.name} returned to the pool`, {
        description: 'The customer keeps the vehicle and its history; it is now deactivated.',
      });
    } catch (caught) {
      toast.error('Could not deallocate', {
        description: caught instanceof Error ? caught.message : 'Something went wrong',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl
          value={state}
          onChange={setState}
          options={STATES}
          stretch={false}
          aria-label="Filter the pool"
        />
        <SearchInput value={search} onChange={setSearch} placeholder="Name, ident or device id…" />
      </div>

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState title="Could not load the pool" message={error.message} />
      ) : devices.length === 0 ? (
        <EmptyState
          icon={Radio}
          title="Nothing in the pool"
          description={
            search || state !== 'all'
              ? 'No device matches this filter.'
              : 'Store the Flespi token and sync, and every device the token can see lands here.'
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead>Ident</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead>Allocation</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.id}>
                  <TableCell>
                    <p className="font-medium">{device.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {device.deviceTypeName ?? 'Unknown type'} · #{device.providerDeviceId}
                    </p>
                  </TableCell>
                  <TableCell>
                    {device.ident ? (
                      <code className="text-xs">{device.ident}</code>
                    ) : (
                      <Badge variant="secondary">No ident</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {device.channel?.name ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {device.lastSeenAt
                      ? formatDistanceToNow(new Date(device.lastSeenAt), { addSuffix: true })
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    {device.organization ? (
                      <div>
                        <p className="text-sm font-medium">{device.organization.name}</p>
                        <p className="text-sm text-muted-foreground">{device.vehicle?.name}</p>
                      </div>
                    ) : (
                      <Badge variant="success">Free</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {device.vehicleId ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => release(device)}
                        disabled={deallocate.isPending}
                      >
                        {deallocate.isPending ? (
                          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                          <Undo2 className="mr-1 h-4 w-4" />
                        )}
                        Deallocate
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAllocating(device)}
                        disabled={!device.ident}
                        title={
                          device.ident ? undefined : 'Flespi has no identifier for this device yet'
                        }
                      >
                        Allocate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <AllocateDialog
        partner={partner}
        device={allocating}
        onOpenChange={(open) => !open && setAllocating(null)}
      />
    </div>
  );
}
